import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FlatList, Platform, Pressable, StyleSheet, View } from "react-native";
import type { HostInstance } from "react-native";

import { useCustomTheme } from "../hooks/useCustomTheme";
import type { IconSet } from "./Icon";
import { Icon } from "./Icon";
import { useRequiredLabel } from "./LabelText";
import { TextInput } from "./TextInput";
import { ThemedDropdown } from "./ThemedDropdown";
import { ThemedText } from "./ThemedText";

type Option<V = string> = {
  label: string;
  renderingLabel?: () => ReactNode;
  value: V;
  disabled?: boolean;
  keepOpenOnSelect?: boolean;
};

export type SelectProps<V = string> = {
  options: Option<V>[];
  placeholder: string;
  disabled?: boolean;
  multiple?: boolean;
  value?: V | V[] | null;
  onChange: (value: V | V[] | null) => void;
  useOverlay?: boolean;
  clearable?: boolean;
  maxSelected?: number;
  error?: string | false;
  icon?: IconSet;
  numColumns?: number;
  dropdownFooter?: ReactNode;
  reserveErrorSpace?: boolean;
  required?: boolean;
  hideRequiredMark?: boolean;
  testID?: string;
};

export function Select<V = string>({
  options,
  placeholder,
  disabled = false,
  multiple = false,
  value = multiple ? ([] as unknown as V[]) : (null as unknown as V | null),
  onChange,
  clearable = true,
  maxSelected,
  error,
  icon,
  numColumns,
  dropdownFooter,
  reserveErrorSpace,
  required = false,
  hideRequiredMark = false,
  testID,
}: SelectProps<V>) {
  type WebKeyDownEvent = {
    nativeEvent?: {
      key?: string;
    };
    preventDefault?: () => void;
  };

  const { colors } = useCustomTheme();
  const requiredLabel = useRequiredLabel();
  const [open, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [focusedOption, setFocusedOption] = useState<string | null>(null);
  const anchorRef = useRef<HostInstance | null>(null);

  const initialSelected = useMemo(() => {
    if (multiple) return (Array.isArray(value) ? value : []) as V[];
    return (value === null || Array.isArray(value) ? null : value) as V | null;
  }, [value, multiple]);

  const [tempSelected, setTempSelected] = useState<V[] | V | null>(
    multiple ? (initialSelected as V[]) : (initialSelected as V | null),
  );

  useEffect(() => {
    setTempSelected((prev) => {
      const next = multiple
        ? (initialSelected as V[])
        : (initialSelected as V | null);
      if (multiple) {
        const prevArr = (Array.isArray(prev) ? prev : []) as V[];
        const nextArr = (Array.isArray(next) ? next : []) as V[];
        if (prevArr.length === nextArr.length) {
          let allEqual = true;
          for (let i = 0; i < prevArr.length; i += 1) {
            if (prevArr[i] !== nextArr[i]) {
              allEqual = false;
              break;
            }
          }
          if (allEqual) {
            return prev;
          }
        }
        return nextArr;
      }
      if (prev === next) {
        return prev;
      }
      return next;
    });
  }, [initialSelected, multiple]);

  useEffect(() => {
    if (!open) {
      setFocusedOption(null);
    }
  }, [open]);

  const shouldKeepDropdownOpenForSelection = useMemo(() => {
    if (multiple) {
      return false;
    }

    const selectedValue = initialSelected as V | null;
    if (selectedValue == null) {
      return false;
    }

    return (
      options.find((option) => option.value === selectedValue)
        ?.keepOpenOnSelect === true
    );
  }, [initialSelected, multiple, options]);

  const previousInitialSelectedRef = useRef(initialSelected);

  useEffect(() => {
    const previousInitialSelected = previousInitialSelectedRef.current;
    previousInitialSelectedRef.current = initialSelected;

    if (!open || multiple) {
      return;
    }

    if (previousInitialSelected === initialSelected) {
      return;
    }

    if (initialSelected == null || shouldKeepDropdownOpenForSelection) {
      return;
    }

    setOpen(false);
  }, [initialSelected, multiple, open, shouldKeepDropdownOpenForSelection]);

  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const triggerStyle =
    Platform.OS === "web"
      ? [
          disabled ? styles.disabled : undefined,
          { outlineStyle: "none" } as any,
        ]
      : disabled
        ? styles.disabled
        : undefined;

  const displayText = useMemo(() => {
    if (multiple) {
      const vals = (tempSelected as V[]) ?? [];
      if (vals.length === 0) return placeholder;
      return vals
        .map((v) => options.find((o) => o.value === v)?.label ?? String(v))
        .join(", ");
    }
    const v = tempSelected as V | null;
    if (v == null) return placeholder;
    return options.find((o) => o.value === v)?.label ?? String(v);
  }, [tempSelected, multiple, options, placeholder]);

  const isSelected = (v: V) => {
    if (multiple) return (tempSelected as V[]).includes(v);
    return tempSelected === v;
  };

  const toggleValue = (option: Option<V>) => {
    const { value: v, keepOpenOnSelect } = option;

    if (multiple) {
      const arr = new Set(tempSelected as V[]);
      if (arr.has(v)) {
        arr.delete(v);
      } else {
        if (maxSelected !== undefined && arr.size >= maxSelected) return;
        arr.add(v);
      }
      setTempSelected(Array.from(arr));
      onChange(Array.from(arr));
    } else {
      setTempSelected(v);
      if (!keepOpenOnSelect) {
        setOpen(false);
      }
      onChange(v);
    }
  };

  const openDropdown = () => {
    if (disabled) return;
    setOpen(true);
  };

  const clearSelection = () => {
    if (disabled) return;
    if (multiple) {
      setTempSelected([]);
      onChange([] as unknown as V[]);
    } else {
      setTempSelected(null);
      onChange(null);
    }
  };

  const runOnSpaceOrEnter = (event: WebKeyDownEvent, action: () => void) => {
    const key = event.nativeEvent?.key;
    if (key !== " " && key !== "Enter") {
      return;
    }

    event.preventDefault?.();
    action();
  };

  const renderOption = ({ item }: { item: Option<V> }) => {
    const optionKey = String(item.value);
    const selected = isSelected(item.value);
    const disabledItem = !!item.disabled;
    const optionFocused = focusedOption === optionKey;
    const optionHovered = hoveredOption === optionKey;
    const optionKeyboardProps = {
      onKeyDown: (event: WebKeyDownEvent) => {
        if (disabledItem) return;
        runOnSpaceOrEnter(event, () => toggleValue(item));
      },
    } as any;

    return (
      <Pressable
        style={[
          styles.option,
          (disabled || disabledItem) && styles.disabled,
          Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : undefined,
          {
            backgroundColor:
              optionHovered || optionFocused
                ? colors.sectionBackground
                : colors.background,
          },
          optionFocused && styles.focusedBorder,
          optionFocused && {
            borderColor: colors.palette.spinach30,
          },
        ]}
        onPress={() => !disabledItem && toggleValue(item)}
        onFocus={() => {
          setFocusedOption(optionKey);
        }}
        onBlur={() => {
          setFocusedOption((current) =>
            current === optionKey ? null : current,
          );
        }}
        onHoverIn={() => {
          setHoveredOption(optionKey);
        }}
        onHoverOut={() => {
          setHoveredOption(null);
        }}
        {...optionKeyboardProps}
      >
        {multiple ? (
          <Icon
            iconSet="material-community"
            icon={selected ? "checkbox-marked" : "checkbox-blank-outline"}
            size={24}
            color={colors.border}
          />
        ) : null}
        {item.renderingLabel?.() ?? (
          <ThemedText style={styles.optionLabel}>{item.label}</ThemedText>
        )}
      </Pressable>
    );
  };

  const currentHasValue = multiple
    ? ((tempSelected as V[])?.length ?? 0) > 0
    : tempSelected != null;

  const slotRight = (
    <>
      {clearable && currentHasValue && (
        <Pressable
          style={styles.clear}
          onPress={(e) => {
            e.stopPropagation?.();
            clearSelection();
          }}
        >
          <Icon
            iconSet="material-community"
            icon="close"
            size={24}
            color={colors.text}
          />
        </Pressable>
      )}
      <Icon
        iconSet="material-community"
        icon="menu-down"
        size={28}
        color={colors.text}
      />
    </>
  );

  const triggerKeyboardProps = {
    onKeyDown: (event: WebKeyDownEvent) => {
      runOnSpaceOrEnter(event, openDropdown);
    },
  } as any;

  return (
    <View style={styles.formGroup}>
      <View>
        <Pressable
          ref={anchorRef}
          disabled={disabled}
          style={triggerStyle}
          onPress={openDropdown}
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          {...triggerKeyboardProps}
          accessibilityRole="button"
          accessibilityLabel={
            required && !disabled ? requiredLabel(placeholder) : placeholder
          }
        >
          <TextInput
            type="text"
            value={currentHasValue ? displayText : ""}
            placeholder={placeholder}
            icon={icon}
            readonly
            error={error}
            reserveErrorSpace={reserveErrorSpace}
            required={required && !hideRequiredMark}
            slotRight={slotRight}
            isFocusedOverride={open || isFocused}
            isHoveredOverride={isHovered}
            testID={testID}
          />
        </Pressable>
      </View>
      <ThemedDropdown
        open={open}
        anchorRef={anchorRef}
        onClose={() => setOpen(false)}
        testIDBackdrop="select-backdrop"
        yOffset={-30}
      >
        <View style={styles.listContainer}>
          <FlatList
            data={options}
            keyExtractor={(o, idx) => String(o.value ?? idx)}
            renderItem={renderOption}
            style={styles.listContainerFlatList}
            numColumns={numColumns}
          />
          {dropdownFooter ? (
            <View style={styles.dropdownFooter}>{dropdownFooter}</View>
          ) : null}
        </View>
      </ThemedDropdown>
    </View>
  );
}

const styles = StyleSheet.create({
  formGroup: {
    marginBottom: 0,
    position: "relative",
  },
  disabled: {
    opacity: 0.5,
  },
  clear: {
    paddingLeft: 6,
  },
  listContainer: {
    gap: 12,
  },
  listContainerFlatList: {
    maxHeight: 320,
  },
  option: {
    paddingHorizontal: 8,
    paddingVertical: 10,
    borderRadius: 4,
    gap: 8,
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  optionLabel: {
    fontSize: 16,
    flexGrow: 1,
  },
  dropdownFooter: {
    paddingTop: 8,
  },
  focusedBorder: {
    borderWidth: 2,
  },
});
